import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import { Tooltip } from 'antd';
import { startCase } from 'lodash';
import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { TaskId } from 'api/models';
import {
  Field,
  FieldAccessMetaInBase3,
  FieldValue,
  FieldValueType,
  FieldWithValue,
} from 'api/tasks';
import { FieldSectionMapping } from 'components/organisms/field-sections/section-list';
import {
  Content,
  FieldGroupLabel,
  FieldGroupRow,
  FieldLabel,
  GhostFields,
  Help,
  LabelContainer,
  Left,
  PrivateIcon,
  Right,
  Row as RlRow,
} from 'components/organisms/task-pane/task-detail/task-fields/styled';
import {
  BooleanField,
  DateField,
  MultiLineTextField,
  MultiSelectField,
  MultiUserField,
  NumberField,
  RatingField,
  SingleSelectField,
  SingleUserField,
  TextField,
} from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields/fields';
import {
  FieldType,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import { updateTaskSagaAction } from 'data/tasks/actions';
import {
  hasReadAccessForField,
  hasWriteAccessForField,
} from 'utils/field-access';
import { unreachable } from 'utils/unreachable';

type Renderer = React.FunctionComponent<any> | null;

export const getFieldRenderer = (field: Field): Renderer => {
  const type = filterableFieldType(field);

  if (field.fieldType === 'RATING') {
    return RatingField;
  }
  switch (type) {
    case FieldType.Text:
      return TextField;
    case FieldType.MultiLineText:
      return MultiLineTextField;
    case FieldType.Number:
      return NumberField;
    case FieldType.Date:
      return DateField;
    case FieldType.Boolean:
      return BooleanField;
    case FieldType.SingleSelect:
      return SingleSelectField;
    case FieldType.MultiSelect:
      return MultiSelectField;
    case FieldType.SingleUser:
      return SingleUserField;
    case FieldType.MultiUser:
    case FieldType.Assignee:
      return MultiUserField;
    case FieldType.Estimate:
      // estimate field type is not supported as custom field
      return unreachable(type as never);
    default:
      return unreachable(type);
  }
};

const booleanAttr = (value: boolean | undefined) => {
  return value != null
    ? value.toString() === 'true'
      ? 'true'
      : 'false'
    : undefined;
};

export const addValuesToFields = (
  fields: Field[],
  values: Array<{ fieldId: number; fieldValue: FieldValueType }> = [],
): FieldWithValue[] => {
  const valueHash = values.reduce((obj, value) => {
    return {
      ...obj,
      [value.fieldId]: value.fieldValue,
    };
  }, {});

  return fields.map((field) => {
    const value = valueHash[field.fieldId];

    return {
      ...field,
      value,
    };
  });
};

interface TaskCustomFieldsProps {
  values: Array<FieldValue<FieldValueType>>;
  fieldSectionMapping: FieldSectionMapping[];
  taskId: TaskId;
  fieldsAccessMetaInBase3?: FieldAccessMetaInBase3;
}

export const TaskCustomFields: FC<TaskCustomFieldsProps> = ({
  fieldSectionMapping,
  taskId,
  fieldsAccessMetaInBase3,
}) => {
  const dispatch = useDispatch();

  const handleOnChange = (fieldValue, { fieldId }: FieldWithValue) => {
    dispatch(
      updateTaskSagaAction({
        projectId: taskId[1],
        taskId: taskId[2],
        data: { fields: [{ fieldId, fieldValue }] },
      }),
    );
  };

  return (
    <>
      {fieldSectionMapping.length ? (
        <>
          {fieldSectionMapping.map((section) => {
            return section.fieldOrder && section.fieldOrder.length ? (
              <React.Fragment key={section.fieldSectionId}>
                {section.fieldOrder.length ? (
                  <GhostFields>
                    <FieldGroupRow>
                      <Left>
                        <LabelContainer>
                          {section.private && (
                            <PrivateIcon>
                              <ViewOff16 />
                            </PrivateIcon>
                          )}
                          <FieldGroupLabel>
                            {section.fieldSectionName}
                          </FieldGroupLabel>
                        </LabelContainer>
                      </Left>
                    </FieldGroupRow>
                    {section.fieldOrder
                      .filter((field) =>
                        hasReadAccessForField(field, fieldsAccessMetaInBase3),
                      )
                      .map((field) => {
                        const FieldRenderer = getFieldRenderer(field);

                        return (
                          <RlRow
                            key={field.fieldId}
                            $private={booleanAttr(field.private)}
                          >
                            <Left>
                              <LabelContainer>
                                <FieldLabel>
                                  {field.fieldLabel ??
                                    startCase(field.fieldName)}
                                  {field.description ? (
                                    <Tooltip title={field.description}>
                                      <Help />
                                    </Tooltip>
                                  ) : null}
                                </FieldLabel>
                              </LabelContainer>
                            </Left>
                            <Right>
                              <Content
                                data-cy={`right.content.custom.fields.${field.fieldLabel}`}
                              >
                                {FieldRenderer ? (
                                  <FieldRenderer
                                    field={field}
                                    onChange={handleOnChange}
                                    disabled={
                                      !hasWriteAccessForField(
                                        field,
                                        fieldsAccessMetaInBase3,
                                      )
                                    }
                                  />
                                ) : null}
                              </Content>
                            </Right>
                          </RlRow>
                        );
                      })}
                  </GhostFields>
                ) : (
                  ''
                )}
              </React.Fragment>
            ) : null;
          })}
        </>
      ) : (
        <></>
      )}
    </>
  );
};
